import type { PendingBirthEntry } from './pendingBirthEntries';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { displayPayload, eventHeads } from './projection';
import type { ClinicalEvent, ClinicalSubject } from './types';
const columns = [
  ['Fetal heart', 'LABOR.FHR'],
  ['Maternal vitals', 'VITALS.'],
  ['Contractions', 'LABOR.CONTRACTIONS'],
  ['Progress', 'LABOR.PROGRESS'],
  ['Status / position', 'LABOR.STATUS', 'LABOR.POSITION', 'LABOR.FETAL_POSITION'],
  ['Membranes / IO', 'LABOR.MEMBRANES', 'ROM', 'IO.'],
  ['Medication / therapy', 'MED.', 'THERAPY'],
];
export function laborBuckets(events: ClinicalEvent[], seconds: number): ClinicalEvent[][] {
  const sorted = [...events].sort(
    (a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at) || a.sequence - b.sequence,
  );
  const groups: ClinicalEvent[][] = [];
  for (const e of sorted) {
    const last = groups.at(-1);
    if (!last || Date.parse(e.occurred_at) - Date.parse(last[0].occurred_at) > seconds * 1000) groups.push([e]);
    else last.push(e);
  }
  return groups;
}
export function LaborGrid({
  events,
  subjects,
  encounterId,
  timeZone,
  onEdit,
  pendingEntries = [],
}: {
  pendingEntries?: PendingBirthEntry[];
  events: ClinicalEvent[];
  subjects: ClinicalSubject[];
  encounterId: string;
  timeZone: string;
  onEdit?: (event: ClinicalEvent) => void;
}) {
  const [seconds, setSeconds] = useState(120),
    [history, setHistory] = useState(false),
    [numbers, setNumbers] = useState(false),
    [selected, setSelected] = useState<ClinicalEvent>();
  const rows = laborBuckets(
    (history ? events : eventHeads(events)).filter(
      (e) =>
        e.encounter_id === encounterId &&
        !(
          subjects.some((s) => s.id === e.subject_id && s.subject_type === 'NEWBORN') &&
          !['APGAR', 'CORD'].includes(e.event_type)
        ) &&
        !e.event_type.startsWith('PRENATAL.') &&
        !e.event_type.startsWith('CHECKLIST.'),
    ),
    seconds,
  );
  const render = (e: ClinicalEvent) => (
    <Button
      type="button"
      key={e.id}
      variant="ghost"
      className="min-h-11 h-auto w-full justify-start whitespace-normal text-left font-normal text-foreground"
      onClick={() => setSelected(e)}>
      <span className="flex flex-col gap-1">
        <span className={e.status === 'STRUCK' ? 'line-through' : ''}>
          {numbers
            ? displayPayload(Object.fromEntries(Object.entries(e.payload).filter(([, v]) => typeof v === 'number'))) ||
              '—'
            : displayPayload(e.payload)}
        </span>
        <span className="text-xs text-muted-foreground">
          {subjects.find((s) => s.id === e.subject_id)?.label} · {e.author_initials} · {e.status}
        </span>
      </span>
    </Button>
  );
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field className="max-w-48">
          <FieldLabel htmlFor="labor-bucket">Group observations within (seconds)</FieldLabel>
          <Input
            id="labor-bucket"
            className="min-h-11"
            type="number"
            min={1}
            max={600}
            value={seconds}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isInteger(n) && n >= 1 && n <= 600) setSeconds(n);
            }}
          />
        </Field>
        <Button type="button" className="min-h-11" variant="outline" onClick={() => setHistory(!history)}>
          {history ? 'Current entries' : 'Show audit entries'}
        </Button>
        <Button type="button" className="min-h-11" variant="outline" onClick={() => setNumbers(!numbers)}>
          {numbers ? 'Show complete findings' : 'Numbers only'}
        </Button>
      </div>
      {pendingEntries.filter((e) => e.encounter_id === encounterId).length > 0 && (
        <section aria-label="Pending local observations" className="rounded border p-3">
          <h4 className="font-semibold">Saved locally — pending server confirmation</h4>
          {pendingEntries
            .filter((e) => e.encounter_id === encounterId)
            .map((e) => (
              <p key={e.id}>
                {e.event_type} · {subjects.find((s) => s.id === e.subject_id)?.label} · {displayPayload(e.payload)} ·{' '}
                {e.pendingState}
              </p>
            ))}
        </section>
      )}
      <Table className="min-w-[1600px]">
        <TableHeader>
          <TableRow>
            <TableHead>Observed time</TableHead>
            {columns.map((c) => (
              <TableHead key={c[0]}>{c[0]}</TableHead>
            ))}
            <TableHead>Other observations</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.flatMap((group, index) => {
            const narrative = group.filter(
              (e) =>
                e.event_type.startsWith('NOTE.') ||
                e.event_type.startsWith('ATTENDANT.') ||
                ['TRANSPORT', 'MILESTONE', 'BIRTH', 'APGAR', 'CORD', 'PLACENTA'].includes(e.event_type),
            );
            const regular = group.filter((e) => !narrative.includes(e));
            return [
              regular.length > 0 ? (
                <TableRow key={`row-${index}`}>
                  <TableCell>{new Date(group[0].occurred_at).toLocaleString(undefined, { timeZone })}</TableCell>
                  {columns.map((c) => (
                    <TableCell key={c[0]} className="min-w-40 align-top">
                      {regular.filter((e) => c.slice(1).some((prefix) => e.event_type.startsWith(prefix))).map(render)}
                    </TableCell>
                  ))}
                  <TableCell>
                    {regular
                      .filter((e) => !columns.some((c) => c.slice(1).some((prefix) => e.event_type.startsWith(prefix))))
                      .map(render)}
                  </TableCell>
                </TableRow>
              ) : null,
              ...narrative.map((e) => (
                <TableRow key={e.id}>
                  <TableCell colSpan={columns.length + 2}>
                    <div className="max-w-[min(70rem,calc(100vw-6rem))] whitespace-normal">
                      <p className="font-medium">
                        {new Date(e.occurred_at).toLocaleString(undefined, { timeZone })} · {e.event_type}
                      </p>
                      {render(e)}
                    </div>
                  </TableCell>
                </TableRow>
              )),
            ];
          })}
        </TableBody>
      </Table>
      <Dialog
        open={!!selected}
        onOpenChange={(open) => {
          if (!open) setSelected(undefined);
        }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selected?.event_type}</DialogTitle>
            <DialogDescription>Source observation, author and audit status</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="flex flex-col gap-3">
              <p>
                {new Date(selected.occurred_at).toLocaleString(undefined, { timeZone })} · {selected.author_initials} ·{' '}
                {selected.status}
              </p>
              <dl>
                {Object.entries(selected.payload).map(([key, value]) => (
                  <div key={key} className="grid grid-cols-2 gap-2">
                    <dt>{key.replaceAll('_', ' ')}</dt>
                    <dd>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</dd>
                  </div>
                ))}
              </dl>
              <p>Recorded {new Date(selected.recorded_at).toLocaleString(undefined, { timeZone })}</p>
              {onEdit && (
                <Button
                  type="button"
                  className="min-h-11"
                  onClick={() => {
                    onEdit(selected);
                    setSelected(undefined);
                  }}>
                  Review / amend entry
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
